import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { EASE } from "@/components/Reveal";
import { scrollToId } from "@/scroll";

const LINKS = [
  { label: "Story", id: "#story" },
  { label: "Products", id: "#products" },
  { label: "Standards", id: "#standards" },
  { label: "CHWA-01", path: "/chwa-01" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const go = (item) => {
    setOpen(false);
    if (item.path) {
      navigate(item.path);
      window.__chwa_lenis?.scrollTo(0, { immediate: true });
      return;
    }
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: item.id } });
    } else {
      scrollToId(item.id);
    }
  };

  return (
    <>
      <header
        data-testid="main-nav"
        className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-800/80 bg-[#0A0A0A]/80 backdrop-blur-md"
      >
        <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-6 md:px-12">
          <button
            data-testid="nav-logo"
            onClick={() => go({ id: "#top" })}
            className="font-display text-xl font-bold tracking-[0.15em] text-zinc-50"
          >
            CHWA
          </button>

          <nav className="hidden items-center gap-10 md:flex">
            {LINKS.map((l) => (
              <button
                key={l.label}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                onClick={() => go(l)}
                className="group relative text-xs uppercase tracking-[0.25em] text-zinc-400 transition-colors duration-300 hover:text-zinc-50"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-zinc-50 transition-[width] duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <button
              data-testid="nav-dealer-cta"
              onClick={() => go({ id: "#dealer" })}
              className="hidden border border-zinc-700 px-5 py-2 text-xs uppercase tracking-[0.25em] text-zinc-100 transition-colors duration-300 hover:border-zinc-300 hover:bg-zinc-100 hover:text-zinc-950 sm:block"
            >
              Become a Dealer
            </button>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(!open)}
              className="text-zinc-100 md:hidden"
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-[#0A0A0A] px-8 md:hidden"
          >
            {[...LINKS, { label: "Become a Dealer", id: "#dealer" }].map((l, i) => (
              <div key={l.label} className="overflow-hidden border-b border-zinc-800">
                <motion.button
                  data-testid={`mobile-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  exit={{ y: "110%" }}
                  transition={{ duration: 0.5, delay: 0.05 * i, ease: EASE }}
                  onClick={() => go(l)}
                  className="block w-full py-5 text-left font-display text-4xl font-semibold uppercase tracking-tight text-zinc-100"
                >
                  {l.label}
                </motion.button>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
